import { inject, Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { ParsedOptionSymbolInterface } from '@modules/options-module/general/interfaces/parsed-option-symbol.interface'
import { OptionsHelperService } from '@modules/options-module/general/services/options-helper.service'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'

@Injectable({
	providedIn: 'root',
})
export class OptionsOrdersHelperService {
	protected readonly map: MapConstructor = Map
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum

	public optionsOrdersResponseMap: Map<
		OptionOrderStateEnum,
		BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>
	> = new this.map<OptionOrderStateEnum, BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>>([
		[
			this.optionOrderStateEnum.OPEN,
			new BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>(null),
		],
		[
			this.optionOrderStateEnum.CANCELED,
			new BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>(null),
		],
		[
			this.optionOrderStateEnum.FILLED,
			new BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>(null),
		],
	])

	public optionsOrdersLoadingMap: Map<OptionOrderStateEnum, BehaviorSubject<boolean>> = new this.map<
		OptionOrderStateEnum,
		BehaviorSubject<boolean>
	>([
		[this.optionOrderStateEnum.OPEN, new BehaviorSubject<boolean>(false)],
		[this.optionOrderStateEnum.CANCELED, new BehaviorSubject<boolean>(false)],
		[this.optionOrderStateEnum.FILLED, new BehaviorSubject<boolean>(false)],
	])

	public isNeedUpdateOptionsOrderResponseMap: Map<OptionOrderStateEnum, BehaviorSubject<boolean>> = new this.map<
		OptionOrderStateEnum,
		BehaviorSubject<boolean>
	>([
		[this.optionOrderStateEnum.OPEN, new BehaviorSubject<boolean>(false)],
		[this.optionOrderStateEnum.CANCELED, new BehaviorSubject<boolean>(false)],
		[this.optionOrderStateEnum.FILLED, new BehaviorSubject<boolean>(false)],
	])

	public optionsOrdersParamsMap: Map<
		OptionOrderStateEnum,
		BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>>
	> = new this.map<OptionOrderStateEnum, BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>>>([
		[
			this.optionOrderStateEnum.OPEN,
			new BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>>({
				status: this.optionOrderStateEnum.OPEN,
				page: 1,
				pageSize: 30,
			}),
		],
		[
			this.optionOrderStateEnum.CANCELED,
			new BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>>({
				status: this.optionOrderStateEnum.CANCELED,
				page: 1,
				pageSize: 30,
			}),
		],
		[
			this.optionOrderStateEnum.FILLED,
			new BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>>({
				status: this.optionOrderStateEnum.FILLED,
				page: 1,
				pageSize: 30,
			}),
		],
	])

	private readonly _optionsHelperService: OptionsHelperService = inject<OptionsHelperService>(OptionsHelperService)

	private _getOptionsOrdersResponseByState$(
		state: OptionOrderStateEnum,
	): BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null> | undefined {
		return this.optionsOrdersResponseMap.get(state)
	}

	public putOrdersFromSocket(orders: OptionOrderInterface[], state: OptionOrderStateEnum): void {
		const optionsOrdersResponse$:
			| BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>
			| undefined = this._getOptionsOrdersResponseByState$(state)
		if (!optionsOrdersResponse$) return

		const currentOrdersResponse: OrdersResponseInterface<OptionOrderInterface> | null =
			optionsOrdersResponse$.getValue()
		if (!currentOrdersResponse) return

		const newOptions: OrdersResponseInterface<OptionOrderInterface> = {
			...currentOrdersResponse,
			orders,
		}

		optionsOrdersResponse$.next(this.formatOrdersOptions(newOptions))
	}

	public setOrderResponseByState(
		orderResponse: OrdersResponseInterface<OptionOrderInterface>,
		state: OptionOrderStateEnum,
	): void {
		const optionsOrdersResponse$:
			| BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>
			| undefined = this._getOptionsOrdersResponseByState$(state)
		if (!optionsOrdersResponse$) return

		optionsOrdersResponse$.next(this.formatOrdersOptions(orderResponse))
	}

	public setLoadingByState(state: OptionOrderStateEnum, value: boolean): void {
		this.optionsOrdersLoadingMap.get(state)?.next(value && !this.optionsOrdersResponseMap.get(state)?.getValue())
	}

	public formatOrdersOptions(
		ordersResponse: OrdersResponseInterface<OptionOrderInterface>,
	): OrdersResponseInterface<OptionOrderInterface> {
		const formattedOptions: OptionOrderInterface[] = ordersResponse.orders.map(
			(order: OptionOrderInterface): OptionOrderInterface => {
				const parsedOptionSymbol: ParsedOptionSymbolInterface | null =
					this._optionsHelperService.parseOptionSymbol(order.symbol)
				if (!parsedOptionSymbol) return order

				return {
					...order,
					...parsedOptionSymbol,
				}
			},
		)

		return {
			...ordersResponse,
			orders: formattedOptions,
		}
	}

	public checkCanEditOrder(order: OptionOrderInterface | null): boolean {
		if (!order) return false
		return order.type !== this.orderTypeEnum.MARKET && [this.orderStatusEnum.NEW].includes(order.status)
	}

	public checkCanCancel(order: OptionOrderInterface | undefined): boolean {
		if (!order) return false

		return ![this.orderStatusEnum.PENDING_CANCEL].includes(order.status)
	}

	public moveOptionOrder(order: OptionOrderInterface, state: OptionOrderStateEnum): void {
		const optionsOrdersResponse$:
			| BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>
			| undefined = this._getOptionsOrdersResponseByState$(state)
		if (!optionsOrdersResponse$) return

		const currentOptionsOrders: OrdersResponseInterface<OptionOrderInterface> | null =
			optionsOrdersResponse$.getValue()
		if (!currentOptionsOrders) return

		const updatedOptionsOrders: OrdersResponseInterface<OptionOrderInterface> = this.formatOrdersOptions({
			...currentOptionsOrders,
			orders: [order, ...(currentOptionsOrders.orders ?? [])],
		})

		optionsOrdersResponse$.next(updatedOptionsOrders)
	}

	public removeOptionOrder(order: OptionOrderInterface, state: OptionOrderStateEnum): void {
		const optionsOrdersResponse$:
			| BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>
			| undefined = this._getOptionsOrdersResponseByState$(state)
		if (!optionsOrdersResponse$) return

		const currentOptionsOrders: OrdersResponseInterface<OptionOrderInterface> | null =
			optionsOrdersResponse$.getValue()
		if (!currentOptionsOrders) return

		const updatedOptionsOrders: OptionOrderInterface[] = currentOptionsOrders.orders.filter(
			(optionOrder: OptionOrderInterface): boolean => optionOrder.id !== order.id,
		)

		optionsOrdersResponse$.next({
			...currentOptionsOrders,
			orders: updatedOptionsOrders,
		})
	}

	public changeOptionOrder(order: OptionOrderInterface, state: OptionOrderStateEnum): void {
		const optionsOrdersResponse$:
			| BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null>
			| undefined = this._getOptionsOrdersResponseByState$(state)
		if (!optionsOrdersResponse$) return

		const currentOptionsOrders: OrdersResponseInterface<OptionOrderInterface> | null =
			optionsOrdersResponse$.getValue()
		if (!currentOptionsOrders) return

		const filteredOptionsOrders: OptionOrderInterface[] = currentOptionsOrders.orders.filter(
			(currentOrder: OptionOrderInterface): boolean => currentOrder.id !== order.id,
		)

		const updatedOptionsOrders: OrdersResponseInterface<OptionOrderInterface> = this.formatOrdersOptions({
			...currentOptionsOrders,
			orders: [order, ...(filteredOptionsOrders ?? [])],
		})

		optionsOrdersResponse$.next(updatedOptionsOrders)
	}
}
